import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  TagsIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Listbox from '@/components/Listbox';
import { useInformationUnitTags } from '@/features/app/api/get-information-unit-tags';
import {
  useInformationUnitTagsByIds,
  useInformationUnitTagsByWorkspaces,
} from '@/features/app/api/get-information-unit-tags-by-workspaces';
import { TInformationUnitTag } from '@/features/app/types';
import { useDebouncedCallback } from '@/hooks/use-debounce-callback';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { TFilterProps } from '@/types';

import {
  activeOptionsStaleTime,
  missingSelected,
  TActiveOptions,
} from '../active-options/active-options';
import { ActiveFacets } from '../active-options/ActiveFacets';
import { ActiveOptionsListbox } from '../active-options/ActiveOptionsListbox';
import { TFacetsResult } from '../active-options/use-mention-facets';

const toTagItem = (tag: TInformationUnitTag) => ({ value: tag.id, label: tag.name });

type TagActiveOptionsProps = {
  activeOptions: TActiveOptions;
  facets: TFacetsResult;
  isDark?: boolean;
  selected: string[];
  onChange: (next: string[]) => void;
  onClose?: () => void;
};

/**
 * Mounted only while the dropdown is open, so opening it is what refreshes stale options.
 * Searched locally: the server search would look through every tag of the workspaces, not
 * through the active ones.
 */
function TagActiveOptions({
  activeOptions,
  facets,
  isDark,
  selected,
  onChange,
  onClose,
}: TagActiveOptionsProps) {
  const { workspaces: selectedWorkspaces } = useProgramBroadcastFilters();

  const scope = useMemo(() => {
    const live = facets.data?.workspaces ?? [];
    if (!selectedWorkspaces.length) return live;
    const liveSet = new Set(live);
    return selectedWorkspaces.filter(id => liveSet.has(id));
  }, [facets.data, selectedWorkspaces]);

  const tags = useInformationUnitTagsByWorkspaces({
    workspaceIds: scope,
    config: { staleTime: activeOptionsStaleTime(activeOptions.source) },
  });

  const active = useMemo(() => {
    const liveTags = new Set(facets.data?.tags ?? []);
    return (scope.length ? (tags.data ?? []) : [])
      .filter(tag => tag.state === 'active' && liveTags.has(tag.id))
      .map(toTagItem);
  }, [facets.data, tags.data, scope]);

  const isFetching = facets.isFetching || tags.isFetching;
  const missing = isFetching ? [] : missingSelected(active, selected);
  const resolved = useInformationUnitTagsByIds(missing);
  const resolvedSelected = useMemo(
    () => resolved.flatMap(({ data }) => (data ? [toTagItem(data)] : [])),
    [resolved]
  );

  return (
    <ActiveOptionsListbox
      isDark={isDark}
      active={active}
      resolvedSelected={resolvedSelected}
      isFetching={isFetching}
      isError={facets.isError || tags.isError}
      selected={selected}
      onChange={onChange}
      onClose={onClose}
    />
  );
}

export function MentionFilterTags({
  isOpen,
  isDark,
  showLabel,
  onToggleFilter,
  onClose,
  activeOptions,
}: TFilterProps & { activeOptions?: TActiveOptions }) {
  const { t } = useTranslation();
  const { tags: state, setFilters: setState } = useProgramBroadcastFilters();
  const { data: tagsData = [] } = useInformationUnitTags({
    config: { enabled: !activeOptions },
  });

  const activeTags = tagsData.filter(tag => tag.state === 'active');

  const allTags = useMemo(() => {
    return activeTags.map(state => ({
      value: state.id,
      label: state.name,
    }));
  }, [tagsData]);

  const handleChangeState = (value: string[]) => {
    setState({ tags: value });
  };

  const handleSearch = (search: string) => {
    setState({ tagSearch: search });
  };

  const debouncedSearch = useDebouncedCallback(handleSearch, 500);

  const ToolTipWrapper = ({ children }: { children: React.ReactNode }) => {
    if (showLabel) return <>{children}</>;
    return (
      <Tooltip help={t('Etiquetas')} grow>
        {children}
      </Tooltip>
    );
  };

  return (
    <div className="relative w-full md:w-auto">
      <div
        onClick={onToggleFilter}
        className={clsx(
          'group flex w-full cursor-pointer items-center border-b-[1px] border-stroke-30-1 p-1',
          { 'border-text-medium-20': isOpen && allTags.length !== 0 && !isDark },
          { 'hover:border-text-medium-20': !isDark },
          { 'border-text-neutral-10 hover:bg-reverse-opacity-05/10': isDark },
          { 'bg-reverse-opacity-05/10': isOpen && isDark }
        )}
      >
        <ToolTipWrapper>
          <TagsIcon
            className={clsx(
              'mr-1',
              {
                'text-text-medium-10': !isOpen && !isDark,
                'text-accent-active-primary-40': isOpen && !isDark,
              },
              {
                'text-text-neutral-10': isDark,
              },
              {
                'group-hover:text-accent-active-primary-40': !isDark,
              }
            )}
          />
        </ToolTipWrapper>
        {showLabel && (
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Medium}
            color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
            className={clsx(
              'select-none pr-size-4',
              {
                'text-text-medium-20': isOpen && !isDark,
              },
              {
                'group-hover:text-text-medium-20': !isDark,
              }
            )}
          >
            {t('Etiquetas')}
          </Typography>
        )}
        {state.length > 0 && (
          <Badge
            variant={!isDark ? BadgeVariantEnum.DefaultFilled : BadgeVariantEnum.NeutralFilled}
            className="py-3"
          >
            + {state.length}
          </Badge>
        )}
      </div>
      {isOpen && (
        <div className="left-0 top-[120%] z-50 w-full bg-neutral-10 p-3 md:absolute md:min-w-[420px] md:shadow-full-medium">
          {activeOptions ? (
            <ActiveFacets activeOptions={activeOptions}>
              {facets => (
                <TagActiveOptions
                  activeOptions={activeOptions}
                  facets={facets}
                  isDark={isDark}
                  selected={state}
                  onChange={handleChangeState}
                  onClose={onClose}
                />
              )}
            </ActiveFacets>
          ) : (
            <Listbox
              dark={isDark}
              mode="multiple"
              items={allTags}
              onChange={value => {
                handleChangeState(value);
              }}
              identifiers={{
                key: 'value',
                value: 'value',
                label: 'label',
                children: 'options',
              }}
              selected={state}
              onClose={onClose}
              onSearch={str => debouncedSearch(str)}
            />
          )}
        </div>
      )}
    </div>
  );
}
